import Chart from "react-apexcharts";
import Skeleton from "react-loading-skeleton";

import styles from "./BreakdownChart.module.scss";

const DESIRED_BAR_HEIGHT_PX = 20;
const CHART_CHROME_PX = 50;

export const MAX_BREAKDOWN_ITEMS = 5;
const PLOT_AREA_HEIGHT = MAX_BREAKDOWN_ITEMS * (DESIRED_BAR_HEIGHT_PX / 0.65);
const FIXED_CHART_HEIGHT = PLOT_AREA_HEIGHT + CHART_CHROME_PX;

export function formatChartValue(val: number) {
  return val?.toLocaleString() ?? "";
}

export type BreakdownChartVars = {
  breakdownPalette: string[];
  textGrey: string;
  gridBorder: string;
};

type BreakdownChartProps = {
  title: string;
  data: { label: string; value: number }[];
  isLoading: boolean;
  chartVars: BreakdownChartVars;
  theme: "light" | "dark";
  emptyText?: string;
};

const BreakdownChart = ({
  title,
  data,
  isLoading,
  chartVars,
  theme,
  emptyText,
}: BreakdownChartProps) => {
  const paddedData = [
    ...data,
    ...Array.from(
      { length: Math.max(0, MAX_BREAKDOWN_ITEMS - data.length) },
      () => ({
        label: "",
        value: 0,
      }),
    ),
  ];

  const barHeightPct = `${Math.round(((DESIRED_BAR_HEIGHT_PX * MAX_BREAKDOWN_ITEMS) / PLOT_AREA_HEIGHT) * 100)}%`;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>{title}</span>
      </div>

      {isLoading && (
        <div className={styles.skeleton}>
          <Skeleton height={FIXED_CHART_HEIGHT} borderRadius={8} />
        </div>
      )}

      {!isLoading && data.length === 0 && (
        <div className={styles.emptyState}>
          {emptyText ?? "No data available for this period."}
        </div>
      )}

      {!isLoading && data.length > 0 && (
        <Chart
          type="bar"
          options={{
            chart: {
              type: "bar",
              toolbar: { show: false },
              fontFamily: "Lato, sans-serif",
              selection: { enabled: false },
            },
            plotOptions: {
              bar: {
                horizontal: true,
                borderRadius: 4,
                distributed: true,
                barHeight: barHeightPct,
              },
            },
            states: {
              active: { filter: { type: "none" } },
              hover: { filter: { type: "none" } },
            },
            colors: chartVars.breakdownPalette,
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: {
              borderColor: chartVars.gridBorder,
              strokeDashArray: 4,
              xaxis: { lines: { show: true } },
              yaxis: { lines: { show: false } },
            },
            xaxis: {
              categories: paddedData.map((d) => d.label),
              labels: {
                style: {
                  colors: chartVars.textGrey,
                  fontSize: "12px",
                  fontFamily: "Lato, sans-serif",
                },
              },
              axisBorder: { show: false },
              axisTicks: { show: false },
            },
            yaxis: {
              labels: {
                style: {
                  colors: chartVars.textGrey,
                  fontSize: "12px",
                  fontFamily: "Lato, sans-serif",
                },
              },
            },
            tooltip: {
              theme,
              y: {
                formatter: formatChartValue,
                title: { formatter: () => "" },
              },
            },
          }}
          series={[{ data: paddedData.map((d) => d.value) }]}
          height={FIXED_CHART_HEIGHT}
        />
      )}
    </div>
  );
};

export default BreakdownChart;
